import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CssBaseline,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  AccessTimeOutlined,
  Add,
  ArticleOutlined,
  AssessmentOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  CommentOutlined,
  EventBusyOutlined,
  InfoOutlined,
  RefreshOutlined,
  ReplayOutlined,
  SendOutlined,
} from "@mui/icons-material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { FilterProvider } from "pages/FilterContext";
import { scrollTop } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import EODCreatePanel from "./EODCreatePanel";
import EODViewPanel from "./EODViewPanel";
import {
  designFontFamily,
  designTheme,
  designWeights,
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";

const dateFormats = ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601];

const EOD_HISTORY_DEFAULT_VIEW_ID = "__default_my_eod_report_history_view__";
const EOD_HISTORY_DEFAULT_HEADER_KEYS = [
  "report_date",
  "report_day",
  "total_worked_hours",
  "achieved_tasks_summary",
  "planned_tasks_summary",
  "submitted_on",
  "status",
  "comments_count",
];

const eodHistoryHeaders = [
  {
    name: "Date",
    key: "report_date",
    type: "date",
    icon: <CalendarMonthOutlined fontSize="small" />,
  },
  {
    name: "Day",
    key: "report_day",
    type: "autocomplete",
    icon: <CalendarMonthOutlined fontSize="small" />,
  },
  {
    name: "Working Hours",
    key: "total_worked_hours",
    type: "time",
    icon: <AccessTimeOutlined fontSize="small" />,
  },
  {
    name: "Today's Work (Achievements)",
    key: "achieved_tasks_summary",
    type: "string",
    icon: <ArticleOutlined fontSize="small" />,
  },
  {
    name: "Tomorrow's Plan",
    key: "planned_tasks_summary",
    type: "string",
    icon: <ArticleOutlined fontSize="small" />,
  },
  {
    name: "Submitted On",
    key: "submitted_on",
    type: "date",
    icon: <CalendarMonthOutlined fontSize="small" />,
  },
  {
    name: "Status",
    key: "status",
    type: "autocomplete",
    icon: <CheckCircleOutline fontSize="small" />,
  },
  {
    name: "Comments",
    key: "comments_count",
    type: "autocomplete",
    icon: <CommentOutlined fontSize="small" />,
  },
];

const resolveEodReportSubModuleId = (userScreenAccess) => {
  const modules = userScreenAccess?.module_details || [];

  for (const module of modules) {
    const match = module?.submodules?.find((submodule) =>
      ["EOD Report", "Daily Reports", "Daily Report"].includes(
        submodule?.sub_module_name,
      ),
    );
    if (match?.sub_module_id) return match.sub_module_id;
  }

  return undefined;
};

const cardSx = {
  border: "1px solid #E2E8F0",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 8px 26px rgba(15, 23, 42, 0.04)",
};

const cellWidths = {
  achieved_tasks_summary: 260,
  planned_tasks_summary: 240,
};

const parseReportDate = (value) => {
  const parsed = moment(value, dateFormats, true);
  return parsed.isValid() ? parsed : moment(value);
};

const formatDisplayDate = (value, output = "DD MMM YYYY") => {
  const parsed = parseReportDate(value);
  return parsed.isValid() ? parsed.format(output) : value || "-";
};

const normalizeStatus = (status) => (status || "Draft").toString().trim();

const getStatusLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === "Reopened") return "Returned";
  if (["Weekoff", "Week Off", "Weekly Off", "Holiday", "Leave"].includes(normalized)) {
    return "No Report Required";
  }
  return normalized;
};

const getStatusStyles = (status) => {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case "Approved":
      return { bgcolor: "#E7F8EE", color: "#078D59" };
    case "Submitted":
      return { bgcolor: "#EAF2FF", color: "#1267FF" };
    case "Reopened":
    case "Returned":
      return { bgcolor: "#FFF0E3", color: "#F97316" };
    case "Weekoff":
    case "Week Off":
    case "Weekly Off":
    case "Holiday":
    case "Leave":
      return { bgcolor: "#F2F4F8", color: "#697386" };
    case "Missed":
    case "Draft":
    default:
      return { bgcolor: "#FFF2DC", color: "#F97316" };
  }
};

const isNonWorkingStatus = (status) =>
  ["Weekoff", "Week Off", "Weekly Off", "Holiday", "Leave"].includes(normalizeStatus(status));

const isReportedStatus = (status) =>
  ["Submitted", "Approved", "Reopened", "Returned"].includes(normalizeStatus(status));

const listFromValue = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .toString()
    .split(/\r?\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatHours = (value) => {
  if (!value || value === "00:00:00" || value === "00:00") return "-";
  const parts = value.toString().split(":");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]} hrs`;
  return value;
};

const getCommentsCount = (row) => {
  if (Number.isFinite(Number(row?.comments_count))) return Number(row.comments_count);
  if (Array.isArray(row?.comments)) return row.comments.length;
  return row?.approver_comments ? 1 : 0;
};

const MetricCard = ({ icon, label, value, sub, color, bg }) => (
  <Box sx={{ ...cardSx, p: 1.5, minHeight: 78, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        bgcolor: bg,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "#1B2445", fontWeight: designWeights.semibold }}>{label}</Typography>
      <Typography sx={{ mt: 0.25, fontSize: 22, lineHeight: 1, color: "#101833", fontWeight: designWeights.heading }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.35, fontSize: 11, color: "#5B6684", fontWeight: designWeights.regular }}>{sub}</Typography>
    </Box>
  </Box>
);

const TaskList = ({ value, empty = "-" }) => {
  const list = listFromValue(value);
  if (!list.length) {
    return <Typography sx={{ fontSize: 12.5, color: "#5B6684", fontWeight: designWeights.regular }}>{empty}</Typography>;
  }

  return (
    <Stack spacing={0.35}>
      {list.map((item, index) => (
        <Typography key={`${item}-${index}`} sx={{ fontSize: 12.5, color: "#17203B", fontWeight: designWeights.regular, lineHeight: 1.45 }}>
          - {item}
        </Typography>
      ))}
    </Stack>
  );
};

const DailyReportContent = ({ subModuleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const history = useHistory();
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // No access check existed here at all -- a viewer-only user (permissions
  // "viewer": true, "editor": false on the "EOD Report" module/submodule)
  // could still see and use "Submit Daily Report" / "Enter Data on Week
  // Off" with no gate. Same editor-permission pattern the My Request
  // screens use, keyed on this module's real module/submodule name
  // (verified against a live payload: module_name "EOD Report",
  // sub_module_name "EOD Report").
  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "EOD Report",
    "EOD Report",
  );
  const [hasProjectAccess, setHasProjectAccess] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [eodReports, setEodReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [currentViewId, setCurrentViewId] = useState(null);
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!userDetails?.id) return;
      try {
        const res = await customGetAPI(`${domain}eod-reports/eod_config/?user_id=${userDetails.id}&company_id=${requestCompanyId}`);
        if (res.ok) {
          const data = res.responseDetails || res.data;
          setHasProjectAccess(!!data?.has_project_access);
        }
      } catch (err) {
        console.error("Error fetching daily report config", err);
      }
    };
    fetchConfig();
  }, [domain, userDetails?.id, requestCompanyId]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customGetAPI(`${domain}eod-reports/?selected_user_id=${userDetails?.id}&company_id=${requestCompanyId}`);
      if (response.ok) {
        const reports = Array.isArray(response.responseDetails)
          ? response.responseDetails
          : response.responseDetails?.results || response.responseDetails?.data || [];
        setEodReports(reports);
      }
    } catch (error) {
      console.error("Fetch daily reports failed", error);
      showToast("error", "Failed to load daily reports");
    } finally {
      setLoading(false);
    }
  }, [domain, userDetails?.id, requestCompanyId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const statusOptions = useMemo(
    () => [...new Set(eodReports.map((row) => normalizeStatus(row.status)).filter(Boolean))],
    [eodReports],
  );

  // Page-level pool: date range + status, before the CustomFilter toolbar
  // (search / column filters / sort) narrows it further.
  const dateStatusFilteredReports = useMemo(() => {
    return eodReports
      .filter((row) => {
        const rowDate = parseReportDate(row.report_date);
        const inDateRange =
          (!startDate || !rowDate.isValid() || !rowDate.isBefore(moment(startDate), "day")) &&
          (!endDate || !rowDate.isValid() || !rowDate.isAfter(moment(endDate), "day"));
        const inStatus = statusFilter === "all" || normalizeStatus(row.status) === statusFilter;
        return inDateRange && inStatus;
      })
      .map((row) => ({
        ...row,
        report_day: formatDisplayDate(row.report_date, "ddd"),
        comments_count: getCommentsCount(row),
        status: normalizeStatus(row.status),
      }));
  }, [eodReports, startDate, endDate, statusFilter]);

  const handleFilteredDataChange = useCallback(
    (nextReports = []) => {
      setFilteredReports(nextReports);
      setPage((prev) => (prev * rowsPerPage >= nextReports.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const summary = useMemo(() => {
    const workingRows = dateStatusFilteredReports.filter((row) => !isNonWorkingStatus(row.status));
    const reportedRows = workingRows.filter((row) => isReportedStatus(row.status));
    const submittedRows = workingRows.filter((row) => normalizeStatus(row.status) === "Submitted");
    const approvedRows = workingRows.filter((row) => normalizeStatus(row.status) === "Approved");
    const returnedRows = workingRows.filter((row) => ["Reopened", "Returned"].includes(normalizeStatus(row.status)));
    const missedRows = workingRows.filter((row) => !isReportedStatus(row.status));
    const percent = (count) => (workingRows.length ? Math.round((count / workingRows.length) * 100) : 0);

    return {
      working: workingRows.length,
      reported: reportedRows.length,
      reportedPercent: percent(reportedRows.length),
      missed: missedRows.length,
      missedPercent: percent(missedRows.length),
      submitted: submittedRows.length,
      submittedPercent: percent(submittedRows.length),
      approved: approvedRows.length,
      approvedPercent: percent(approvedRows.length),
      returned: returnedRows.length,
      returnedPercent: percent(returnedRows.length),
      compliance: percent(reportedRows.length),
    };
  }, [dateStatusFilteredReports]);

  const visibleRows = filteredReports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const reCallApi = (shouldRefetch) => {
    if (shouldRefetch) fetchReports();
  };

  const openCreatePanel = () => setIsCreatePanelOpen(true);
  const closeCreatePanel = () => setIsCreatePanelOpen(false);

  const openViewPanel = (row) => {
    if (row?.id && !isNonWorkingStatus(row.status)) {
      setSelectedReportId(row.id);
      setIsViewPanelOpen(true);
    }
  };

  const closeViewPanel = () => {
    setIsViewPanelOpen(false);
    setSelectedReportId(null);
  };

  const handleReportDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    setDateRangeLabel(rangeLabel || "Custom Range");
    setStartDate(moment(new Date(rangeStart)).format("YYYY-MM-DD"));
    setEndDate(moment(new Date(rangeEnd)).format("YYYY-MM-DD"));
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const canPersistPreferences = Boolean(domain && userDetails?.id && subModuleId);

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "my_eod_report_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: EOD_HISTORY_DEFAULT_VIEW_ID,
  });

  const persistedDefaultViewId = activeViewId || EOD_HISTORY_DEFAULT_VIEW_ID;

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== EOD_HISTORY_DEFAULT_VIEW_ID) return prev;
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => {
    const defaultKeys = new Set(EOD_HISTORY_DEFAULT_HEADER_KEYS);
    return eodHistoryHeaders.filter((header) => defaultKeys.has(header.key));
  }, []);

  const storedDefaultView = useMemo(
    () => savedViewsForScope.find((view) => view.id === EOD_HISTORY_DEFAULT_VIEW_ID) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () => savedViewsForScope.filter((view) => view.id !== EOD_HISTORY_DEFAULT_VIEW_ID),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: EOD_HISTORY_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === EOD_HISTORY_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return customSavedViews.find((view) => view.id === effectiveViewId) || defaultView;
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(eodHistoryHeaders.map((header) => [header.key, header]));

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return { ...baseHeader, name: entry.name?.trim() || baseHeader.name };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = eodHistoryHeaders.filter((header) => selectedKeys.has(header.key));
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(EOD_HISTORY_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === EOD_HISTORY_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(EOD_HISTORY_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(EOD_HISTORY_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || EOD_HISTORY_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(EOD_HISTORY_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={eodHistoryHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={canPersistPreferences}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === EOD_HISTORY_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      canPersistPreferences,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Working Days": summary.working,
      Reported: summary.reported,
      Missed: summary.missed,
      Compliance: `${summary.compliance}%`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, summary, userDetails],
  );

  const renderEodCell = (row, head, nonWorking, commentsCount) => {
    switch (head.key) {
      case "report_date":
        return (
          <Typography sx={{ fontSize: 12.5, fontWeight: designWeights.medium }}>
            {formatDisplayDate(row.report_date, "DD MMM YYYY")}
          </Typography>
        );
      case "report_day":
        return (
          <Typography sx={{ fontSize: 12.5, fontWeight: designWeights.regular }}>
            {formatDisplayDate(row.report_date, "ddd")}
          </Typography>
        );
      case "total_worked_hours":
        return nonWorking ? "-" : formatHours(row.total_worked_hours);
      case "achieved_tasks_summary":
        return nonWorking ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthOutlined sx={{ color: "#64748B", fontSize: 22 }} />
            <Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: designWeights.semibold }}>
                {["Weekoff", "Weekly Off"].includes(normalizeStatus(row.status)) ? "Week Off" : normalizeStatus(row.status)}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#64748B", fontWeight: designWeights.regular }}>No report required</Typography>
            </Box>
          </Stack>
        ) : (
          <TaskList value={row.achieved_tasks_summary} />
        );
      case "planned_tasks_summary":
        return <TaskList value={row.planned_tasks_summary} />;
      case "submitted_on":
        return isReportedStatus(row.status) ? (
          <>
            <Typography sx={{ fontSize: 12.5, fontWeight: designWeights.medium }}>
              {formatDisplayDate(row.submitted_on || row.updated_at || row.report_date, "DD MMM YYYY")}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: 11.5, color: "#56617E", fontWeight: designWeights.regular }}>
              {row.submitted_time || row.updated_time || ""}
            </Typography>
          </>
        ) : (
          "-"
        );
      case "status":
        return (
          <>
            <Chip
              label={getStatusLabel(row.status)}
              size="small"
              sx={{
                ...getStatusStyles(row.status),
                height: 28,
                borderRadius: "6px",
                fontSize: 12,
                fontWeight: designWeights.semibold,
              }}
            />
            {row.approved_by ? (
              <Typography sx={{ mt: 0.35, fontSize: 11, color: "#56617E", fontWeight: designWeights.regular }}>by {row.approved_by}</Typography>
            ) : null}
          </>
        );
      case "comments_count":
        return commentsCount ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CommentOutlined sx={{ fontSize: 17, color: "#1B4ED8" }} />
            <Typography sx={{ fontSize: 12, fontWeight: designWeights.semibold }}>{commentsCount}</Typography>
          </Stack>
        ) : (
          "-"
        );
      default:
        return row[head.key] ?? "-";
    }
  };

  const DailyReportMobileCard = ({ row }) => {
    const nonWorking = isNonWorkingStatus(row.status);
    const commentsCount = getCommentsCount(row);
    const statusHead = visibleHeaders.find((head) => head.key === "status");
    const detailHeads = visibleHeaders.filter(
      (head) => !["report_date", "report_day", "status"].includes(head.key),
    );

    return (
      <Box
        onClick={() => openViewPanel(row)}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          p: 1.5,
          bgcolor: nonWorking ? "#F5F6F8" : "#FFFFFF",
          color: nonWorking ? "#9AA3B2" : "inherit",
          cursor: nonWorking ? "default" : "pointer",
        }}
      >
        <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: designWeights.heading, color: "#101833" }}>
              {renderEodCell(row, { key: "report_date" }, nonWorking, commentsCount)}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, color: "#56617E" }}>
              {renderEodCell(row, { key: "report_day" }, nonWorking, commentsCount)}
            </Typography>
          </Box>
          {statusHead ? (
            <Box sx={{ flexShrink: 0 }}>
              {renderEodCell(row, statusHead, nonWorking, commentsCount)}
            </Box>
          ) : null}
        </Stack>
        <Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: "1px solid #E8EEF5",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.25,
          }}
        >
          {detailHeads.map((head) => (
            <Box key={head.key} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 10.5, color: "#56617E", fontWeight: designWeights.semibold }}>
                {head.name}
              </Typography>
              <Box sx={{ mt: 0.25, fontSize: 12.5, color: "#1B2445", minWidth: 0 }}>
                {renderEodCell(row, head, nonWorking, commentsCount)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (loading) return <Loader />;

  return (
    <Box sx={{ width: "100%", minHeight: "100%", p: { xs: 1.5, md: 3 }, color: "#101833", fontFamily: designFontFamily }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "flex-start" }} spacing={2}>
        <Box>
          <GlobalBreadcrumb
            sx={{ mb: 1.5 }}
            trail={[
              {
                label: "My Workspace",
                onClick: () => history.push("/admin/myzone/status"),
              },
              "Daily Report",
            ]}
          />
          <Typography sx={{ fontSize: 28, lineHeight: 1.1, fontWeight: designWeights.heading, color: "#101833" }}>
            Daily Report
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13, color: "#56617E", fontWeight: designWeights.regular }}>
            Record your daily work, plan tomorrow's tasks and keep track of your submitted reports.
          </Typography>
        </Box>
        {checkAccess ? (
          <PrimaryButton
            startIcon={<Add sx={{ fontSize: 18 }} />}
            sx={{ width: { xs: "100%", md: "auto" }, minWidth: HEADER_ACTION_BUTTON_WIDTH }}
            onClick={openCreatePanel}
          >
            Submit Daily Report
          </PrimaryButton>
        ) : null}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mt: 2.5 }}>
        <Box sx={{ width: { xs: "100%", sm: 320 } }}>
          <ApprovalDateRangeFilter
            thisMonthToDate
            startDate={startDate}
            endDate={endDate}
            dateRangeLabel={dateRangeLabel}
            onChange={handleReportDateRange}
            includeCurrentDate
            sx={{ width: "100%", minHeight: 42, justifyContent: "space-between" }}
          />
        </Box>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: { xs: "100%", sm: 190 }, "& .MuiOutlinedInput-root": { borderRadius: "6px", fontWeight: designWeights.medium } }}
        >
          <MenuItem value="all">All Status</MenuItem>
          {statusOptions.map((status) => (
            <MenuItem key={status} value={status}>
              {getStatusLabel(status)}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchReports} sx={{ width: 42, height: 42, border: "1px solid #D9E2EF", borderRadius: "6px" }}>
            <RefreshOutlined />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1.15fr 1.15fr 0.55fr" },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <Box sx={{ ...cardSx, p: 1.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: designWeights.heading }}>DAY DISPOSITION</Typography>
            <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: designWeights.regular }}>(Based on working days)</Typography>
            <InfoOutlined sx={{ fontSize: 15, color: "#64748B" }} />
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.25 }}>
            <MetricCard icon={<CalendarMonthOutlined />} label="Working Days" value={summary.working} sub="This Period" color="#079B63" bg="#E7F8EE" />
            <MetricCard icon={<ArticleOutlined />} label="Reported" value={summary.reported} sub={`${summary.reportedPercent}%`} color="#1267FF" bg="#EAF2FF" />
            <MetricCard icon={<EventBusyOutlined />} label="Missed" value={summary.missed} sub={`${summary.missedPercent}%`} color="#EF4444" bg="#FFECEC" />
          </Box>
          <Typography sx={{ mt: 1.25, fontSize: 11.5, color: "#56617E", fontWeight: designWeights.regular }}>
            Working Days = Reported + Missed. Weekly offs and holidays are not counted.
          </Typography>
        </Box>

        <Box sx={{ ...cardSx, p: 1.5, borderColor: "#F4C791" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: designWeights.heading }}>REPORT STATUS</Typography>
            <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: designWeights.regular }}>(For reported days)</Typography>
            <InfoOutlined sx={{ fontSize: 15, color: "#64748B" }} />
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.25 }}>
            <MetricCard icon={<SendOutlined />} label="Submitted" value={summary.submitted} sub={`${summary.submittedPercent}%`} color="#1267FF" bg="#EAF2FF" />
            <MetricCard icon={<CheckCircleOutline />} label="Approved" value={summary.approved} sub={`${summary.approvedPercent}%`} color="#1267FF" bg="#EAF2FF" />
            <MetricCard icon={<ReplayOutlined />} label="Returned" value={summary.returned} sub={`${summary.returnedPercent}%`} color="#F97316" bg="#FFF0E3" />
          </Box>
        </Box>

        <Box sx={{ ...cardSx, p: 1.5, display: "flex", alignItems: "center", gap: 1.75 }}>
          <Box sx={{ width: 54, height: 54, borderRadius: "8px", bgcolor: "#E6FAF5", color: "#0AAE87", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AssessmentOutlined sx={{ fontSize: 34 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#1B2445", fontWeight: designWeights.semibold }}>Compliance</Typography>
            <Typography sx={{ mt: 0.25, fontSize: 26, lineHeight: 1, color: "#101833", fontWeight: designWeights.heading }}>
              {summary.compliance}%
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 11, color: "#56617E", fontWeight: designWeights.regular }}>Based on working days</Typography>
          </Box>
        </Box>
      </Box>

      <Alert
        icon={<InfoOutlined />}
        severity="info"
        sx={{
          mt: 1.5,
          border: "1px solid #B9D7FF",
          bgcolor: "#F8FBFF",
          color: "#1B2445",
          alignItems: "center",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: designWeights.heading }}>How it works</Typography>
            <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: designWeights.regular }}>
              Reports are expected only on working days. Weekly offs and holidays do not require a report and are not included in compliance.
            </Typography>
          </Box>
          {checkAccess ? (
            <Button
              variant="outlined"
              startIcon={<CalendarMonthOutlined />}
              onClick={openCreatePanel}
              sx={{
                width: { xs: "100%", md: "auto" },
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: designWeights.semibold,
                bgcolor: "#FFFFFF",
              }}
            >
              Enter Data on Week Off
            </Button>
          ) : null}
        </Stack>
      </Alert>

      <Paper sx={{ ...cardSx, mt: 1.5, overflow: "hidden" }}>
        <CustomFilter
          title="Daily Report History"
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={dateStatusFilteredReports}
          filteredData={filteredReports}
          onFilteredDataChange={handleFilteredDataChange}
          screenAccess
          showSaveButton={canPersistPreferences}
          hideCount
          downloadFormats={["excel", "pdf", "csv"]}
          includeMetadata
          metadata={reportMetadata}
          labeledToolbar
          searchPlaceholder="Search in reports..."
        />

        <Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1.25, p: 1.5, pt: 0 }}>
          {!visibleRows.length ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                color: "#56617E",
                border: "1px dashed #E2E8F0",
                borderRadius: "8px",
              }}
            >
              No Daily Reports Found
            </Box>
          ) : (
            visibleRows.map((row) => (
              <DailyReportMobileCard key={row.id || row.report_date} row={row} />
            ))
          )}
        </Box>
        <TableContainer ref={containerRef} sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
          <Table size="small" sx={{ minWidth: 1180 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F5F7FB" }}>
                {visibleHeaders.map((head) => (
                  <TableCell key={head.key} sx={{ py: 1.25, color: "#1B2445", fontSize: 12, fontWeight: designWeights.heading, borderBottom: "1px solid #E2E8F0" }}>
                    {head.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!visibleRows.length ? (
                <TableEmptyState
                  colSpan={visibleHeaders.length}
                  dataLength={dateStatusFilteredReports}
                  title="No Daily Reports Found"
                  subtitle="There's nothing to show for the selected period yet."
                />
              ) : (
                visibleRows.map((row) => {
                  const nonWorking = isNonWorkingStatus(row.status);
                  const commentsCount = getCommentsCount(row);
                  return (
                    <TableRow
                      hover={!nonWorking}
                      key={row.id || row.report_date}
                      onClick={() => openViewPanel(row)}
                      sx={{
                        cursor: nonWorking ? "default" : "pointer",
                        "& .MuiTableCell-root": {
                          py: 1.15,
                          borderBottom: "1px solid #E8EEF5",
                          backgroundColor: nonWorking ? "#F5F6F8" : "transparent",
                          color: nonWorking ? "#9AA3B2" : "#1B2445",
                          fontSize: 12.5,
                          fontWeight: designWeights.regular,
                          verticalAlign: "top",
                        },
                      }}
                    >
                      {visibleHeaders.map((head) => (
                        <TableCell
                          key={head.key}
                          align={head.key === "comments_count" ? "center" : "left"}
                          sx={cellWidths[head.key] ? { maxWidth: cellWidths[head.key] } : undefined}
                        >
                          {renderEodCell(row, head, nonWorking, commentsCount)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2, py: 0.75, borderTop: "1px solid #E8EEF5", backgroundColor: "#FFFFFF" }}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: filteredReports.length,
            itemLabel: "reports",
            onPageChange: setPage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            rowsPerPageOptions,
          })}
        </Box>
      </Paper>

      <Box
        sx={{
          ...cardSx,
          mt: 1.5,
          px: 2,
          py: 1.25,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 34, height: 34, borderRadius: "6px", bgcolor: "#EAF2FF", color: "#1267FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InfoOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12.5, color: "#1B2445", fontWeight: designWeights.semibold }}>Approval workflow for Daily Report uses the shared Request Engine.</Typography>
            <Typography sx={{ fontSize: 11.5, color: "#56617E", fontWeight: designWeights.regular }}>Approve, return and resubmit actions follow the same flow as other requests.</Typography>
          </Box>
        </Stack>
        <Button sx={{ textTransform: "none", fontWeight: designWeights.semibold }}>Learn more</Button>
      </Box>

      {isCreatePanelOpen ? (
        <EODCreatePanel
          open={isCreatePanelOpen}
          onClose={closeCreatePanel}
          reCallApi={reCallApi}
          hasProjectAccess={hasProjectAccess}
        />
      ) : null}
      {isViewPanelOpen ? (
        <EODViewPanel
          open={isViewPanelOpen}
          onClose={closeViewPanel}
          reportId={selectedReportId}
          reCallApi={reCallApi}
          hasProjectAccess={hasProjectAccess}
          checkAccess={checkAccess}
        />
      ) : null}
    </Box>
  );
};

export const EODReports = () => {
  const { domain, userDetails, userScreenAccess } = useSelector((state) => state.tokenReducer);
  const subModuleId = useMemo(
    () => resolveEodReportSubModuleId(userScreenAccess),
    [userScreenAccess],
  );

  return (
    <ThemeProvider theme={designTheme}>
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        <CssBaseline />
        <DailyReportContent subModuleId={subModuleId} />
      </FilterProvider>
    </ThemeProvider>
  );
};

export default EODReports;
